import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Subscription} from 'rxjs';
import {ReferralApiService} from '../../../shared/services/referral-api.service';
import {StageService} from '../../../shared/services/stage.service';
import {ReasonService} from '../../../shared/services/reason.service';
import {SrMappingService} from '../../../shared/services/sr-mapping.service';
import {RefPromptService} from '../shared/ref-prompt.component';
import {cap, PER_PAGE} from '../shared/ref-utils';

export const REFERRAL_STATUS_LABELS: { [k: string]: string } = {
    PENDING_REGISTRATION: 'Pending registration',
    REGISTERED: 'Registered',
    PENDING: 'Pending',
    CONVERTED: 'Converted',
    CLOSED: 'Closed',
    REJECTED: 'Rejected',
    UNDER_REVIEW: 'Under review'
};
export const REFERRAL_STATUS_KEYS = Object.keys(REFERRAL_STATUS_LABELS);
// Only these can be set from the admin panel (backend rejects any other target status).
export const REFERRAL_MANUAL_STATUSES = ['REGISTERED', 'PENDING', 'CONVERTED', 'CLOSED'];

export interface ApiReferrer {
    id: number;
    name: string;
    referral_code: string;
    mobile?: string;
    email?: string;
    status?: string;
}

export interface ApiFraudFlag {
    id: number;
    type: string;
    severity: string;
    status: string;
    reason?: string;
    resolution_action?: string;
    resolution_reason?: string;
    reviewed_at?: string;
    created_at?: string;
}

export interface ApiReferral {
    id: number;
    reference: string;
    referrer: ApiReferrer;
    referred_user_id: number;
    referred_name: string;
    source: string;
    status: string;
    fraud_status: string;
    created_at: string;
    updated_at: string;
    lead_id?: number;
    rewards?: any[];
    fraud_flags?: ApiFraudFlag[];
    registered_at?: string;
    converted_at?: string;
    closed_at?: string;
    rejection_reason?: string;
}

// A flag in one of these statuses still needs an admin decision (see FraudFlag::ACTIVE_STATUSES on the backend).
export const FRAUD_FLAG_ACTIVE_STATUSES = ['OPEN', 'REWARD_HELD'];
const FRAUD_ACTION_LABELS: { [k: string]: string } = {
    CLEAR: 'Clear this flag',
    HOLD_REWARD: 'Hold the reward',
    BLOCK_REFERRAL: 'Block this referral',
    REJECT: 'Reject this referral'
};

@Component({
    selector: 'app-referral-list',
    templateUrl: './referral-list.component.html'
})
export class ReferralListComponent implements OnInit, OnDestroy {
    q = '';
    status = '';
    page = 0;
    per = PER_PAGE;
    total = 0;
    list: ApiReferral[] = [];
    loading = false;

    selected: ApiReferral = null;
    loadingDetail = false;

    STATUS = REFERRAL_STATUS_LABELS;
    STATUS_KEYS = REFERRAL_STATUS_KEYS;
    MANUAL_STATUSES = REFERRAL_MANUAL_STATUSES;
    FRAUD_ACTIVE_STATUSES = FRAUD_FLAG_ACTIVE_STATUSES;
    cap = cap;

    modalType: any;
    followUpForm: FormGroup;
    allStages: any[] = [];
    allReasons: any[] = [];
    reasonFollowups: any[] = [];
    followUps: any[] = [];
    followUpTarget: ApiReferral = null;
    followUpTypeFilter = '';
    followUpOldestFirst = false;
    submitted = false;
    submitting = false;

    private subs: Subscription[] = [];

    constructor(private referralApi: ReferralApiService,
                private stageService: StageService,
                private reasonService: ReasonService,
                private srMappingService: SrMappingService,
                private route: ActivatedRoute,
                private prompt: RefPromptService,
                private fb: FormBuilder,
                public modalService: NgbModal) {
    }

    ngOnInit() {
        this.followUpForm = this.fb.group({
            subject: ['', Validators.required],
            stage_id: ['', Validators.required],
            reason_id: ['', Validators.required],
            type: ['', Validators.required],
            remark: ['']
        });

        this.getAllStages();
        this.getAllReasons();

        // Dashboard links pre-filter this list via query params
        this.subs.push(this.route.queryParamMap.subscribe(qp => {
            this.status = qp.get('status') || '';
            this.page = 0;
            this.fetch();
        }));
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
    }

    onFilter() {
        this.page = 0;
        this.fetch();
    }

    onPageChange(page: number) {
        this.page = page;
        this.fetch();
    }

    fetch() {
        this.loading = true;
        const params: any = {
            page: this.page + 1,
            per_page: this.per
        };
        if (this.status) {
            params.status = this.status;
        }
        const q = this.q.trim();
        if (q) {
            // The table's visible identifier is the per-referral reference (REF-XXXXXXXX); a bare
            // partner code (e.g. EMR7JG4ZY) finds every referral made under that code instead.
            if (/^ref-?/i.test(q)) {
                params.reference = q.toUpperCase();
            } else {
                params.referral_code = q.toUpperCase();
            }
        }
        this.referralApi.getReferrals(params).subscribe(res => {
            this.list = (res && res.data) ? res.data : [];
            this.total = res ? res.total : 0;
            this.loading = false;
        }, () => {
            this.list = [];
            this.total = 0;
            this.loading = false;
        });
    }

    isManualStatus(status: string): boolean {
        return this.MANUAL_STATUSES.includes(status);
    }

    async changeStatus(r: ApiReferral, to: string, sel: HTMLSelectElement) {
        let reason = '';
        if (to === 'CLOSED') {
            const entered = await this.prompt.ask({
                title: `Close referral ${r.reference}?`,
                text: 'Optionally record why this referral is being closed.',
                label: 'Reason (optional)',
                okText: 'Close referral',
                check: () => ''
            });
            if (entered === null) {
                sel.value = r.status;
                return;
            }
            reason = entered;
        }
        const prevStatus = r.status;
        this.referralApi.updateStatus(r.id, reason ? {status: to, reason} : {status: to}).subscribe(() => {
            r.status = to;
            if (this.selected && this.selected.id === r.id) {
                this.openDetail(r);
            }
        }, () => {
            sel.value = prevStatus;
        });
    }

    openDetail(r: ApiReferral) {
        this.loadingDetail = true;
        this.selected = r;
        this.referralApi.getReferral(r.id).subscribe(detail => {
            this.selected = detail;
            this.loadingDetail = false;
        }, () => {
            this.loadingDetail = false;
        });
    }

    close() {
        this.selected = null;
    }

    isFraudFlagActive(flag: ApiFraudFlag): boolean {
        return this.FRAUD_ACTIVE_STATUSES.includes(flag.status);
    }

    async resolveFlag(r: ApiReferral, flag: ApiFraudFlag, action: string) {
        const label = FRAUD_ACTION_LABELS[action] || action;
        const reason = await this.prompt.ask({
            title: `${label}?`,
            text: `This resolves the "${cap((flag.type || '').toLowerCase().replace(/_/g, ' '))}" fraud flag on ${r.reference}.`,
            label: 'Reason (required)',
            okText: label,
            check: v => (v || '').trim().length >= 3 ? '' : 'Enter at least 3 characters explaining your decision.'
        });
        if (reason === null) {
            return;
        }
        this.referralApi.resolveFraudFlag(flag.id, {action, reason}).subscribe(() => {
            this.openDetail(r);
            this.fetch();
        });
    }

    fmt(iso: string): string {
        return iso ? new Date(iso).toLocaleDateString('en-IN', {day: 'numeric', month: 'short', year: 'numeric'}) : '';
    }

    ago(iso: string): string {
        if (!iso) {
            return '';
        }
        const d = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
        return d <= 0 ? 'Today' : d === 1 ? 'Yesterday' : d + ' days ago';
    }

    /* ---------------- Follow up ---------------- */

    getAllStages() {
        this.allStages = [];
        this.stageService.get({page: -1}).subscribe(data => {
            this.allStages = (data && data['data'] && data['data'].length > 0) ? data['data'] : [];
        });
    }

    getAllReasons() {
        this.allReasons = [];
        this.reasonService.get({page: -1}).subscribe(data => {
            this.allReasons = (data && data['data'] && data['data'].length > 0) ? data['data'] : [];
        });
    }

    getReasonsFollowups() {
        this.followUpForm.patchValue({reason_id: ''});
        this.reasonFollowups = [];
        const params = {
            stages_id: (this.followUpForm && this.followUpForm.value && this.followUpForm.value.stage_id) ? this.followUpForm.value.stage_id : ''
        };
        this.srMappingService.get(params).subscribe(data => {
            this.reasonFollowups = (data && data['data'] && data['data'].length > 0) ? data['data'] : [];
        });
    }

    openFollowUp(content, r: ApiReferral) {
        this.followUpTarget = r;
        this.submitted = false;
        this.followUpForm.reset();
        this.reasonFollowups = [];
        this.modalType = 'FOLLOW_UP';
        this.modalService.open(content, {size: 'lg'});
    }

    followUp() {
        this.submitted = true;
        if (!this.followUpForm.valid) {
            return;
        }
        const params = {
            stage_id: this.followUpForm.value.stage_id || '',
            reason_id: this.followUpForm.value.reason_id || '',
            type: this.followUpForm.value.type || '',
            subject: this.followUpForm.value.subject || '',
            remark: this.followUpForm.value.remark || ''
        };
        this.submitting = true;
        this.referralApi.followUp(this.followUpTarget.id, params).subscribe(() => {
            this.submitting = false;
            this.submitted = false;
            this.followUpForm.reset();
            this.modalService.dismissAll('');
            this.fetch();
        }, () => {
            this.submitting = false;
        });
    }

    viewFollowUpRemarks(content, r: ApiReferral) {
        this.referralApi.getFollowUp(r.id).subscribe(data => {
            this.followUps = (data && data['data'] && data['data'].length > 0) ? data['data'] : [];
            this.followUpTypeFilter = '';
            this.followUpOldestFirst = false;
            this.modalType = 'VIEW_FOLLOW_UP_REMARKS';
            this.modalService.open(content, {size: 'lg', windowClass: 'custom-modal'});
        });
    }

    /* ---------------- Follow up timeline ---------------- */

    get allFollowUpItems(): any[] {
        const items: any[] = [];
        (this.followUps || []).forEach(group => (group.lead_details || []).forEach(item => items.push(item)));
        return items;
    }

    get followUpItems(): any[] {
        const all = this.allFollowUpItems;
        const filtered = this.followUpTypeFilter ? all.filter(i => i.type === this.followUpTypeFilter) : all;
        return this.followUpOldestFirst ? [...filtered].reverse() : filtered;
    }

    followUpCount(type: string): number {
        return this.allFollowUpItems.filter(i => i.type === type).length;
    }

    followUpTypeIcon(type: string): string {
        return {CALL: 'fas fa-phone-alt', WHATS_APP: 'fab fa-whatsapp', TEXT_MESSAGE: 'fas fa-comment-dots'}[type] || 'fas fa-circle';
    }

    followUpTypeLabel(type: string): string {
        return {CALL: 'Call', WHATS_APP: 'WhatsApp', TEXT_MESSAGE: 'Text message'}[type] || type || 'Follow up';
    }
}
